// Главный поток: один на страницу воркер vbg (v5 либо v2) и одна активная сессия обработки.
// Движок держит воркер и модель живыми между сессиями: start/stop лишь включают и гасят подачу
// кадров, поэтому переключение камеры не перезагружает wasm с моделью.

import { endEmptyAudioTrack, startEmptyAudioTrack } from "../EmptyAudioTrack";
import { log } from "../logger";
import { browserName } from "../platform";
import type {
  VbgConfigMessage,
  VbgConfigPatch,
  VbgDelegate,
  VbgLogMessage,
  VbgOutgoingMessage,
  VbgPreloadMessage,
  VbgStartMessage,
  VbgStopMessage,
} from "./protocol";

/** Кадр в работе ровно один: пока воркер не вернул `result`, следующий не отправляется. */
const PUMP_INTERVAL_MS = 1000 / 30;
/** Границы ожиданий: холодный старт трека и добор последнего кадра при остановке. */
const METADATA_TIMEOUT_MS = 8000;
const DRAIN_TIMEOUT_MS = 1000;

// DEV-оверлей: FPS обработки BlurProcessor считаем по кадрам, вернувшимся из воркера.
let fpsFrames = 0;
let fpsWindowStart = 0;
let fpsOverlay: HTMLDivElement | null = null;

function reportProcessedFrame(): void {
  if (!import.meta.env.DEV) return;
  fpsFrames += 1;
  const now = performance.now();
  if (fpsWindowStart === 0) fpsWindowStart = now;
  const elapsed = now - fpsWindowStart;
  if (elapsed < 500) return;
  const fps = (fpsFrames * 1000) / elapsed;
  fpsFrames = 0;
  fpsWindowStart = now;
  if (!fpsOverlay) {
    fpsOverlay = document.createElement("div");
    fpsOverlay.style.cssText =
      "position:fixed;top:8px;left:8px;z-index:2147483647;padding:4px 8px;" +
      "background:rgba(0,0,0,.6);color:#4ade80;font:12px/1.4 monospace;" +
      "border-radius:4px;pointer-events:none;";
    document.body.append(fpsOverlay);
  }
  fpsOverlay.textContent = `BlurProcessor: ${fps.toFixed(1)} fps`;
}

export interface VbgStartOptions {
  track: MediaStreamTrack;
  mode: "blur" | "background";
  blur: number;
  backgroundUrl?: string | null;
}

interface VbgSession {
  video: HTMLVideoElement;
  ctx: CanvasRenderingContext2D;
  outputTrack: MediaStreamTrack;
  awaiting: boolean;
  /** Кадр источника пришёл, пока воркер был занят: заберём его сразу по возвращении результата. */
  pending: boolean;
  /** Ненулевые, пока работает соответствующий механизм подачи кадров. */
  rvfc: number;
  timer: number;
  /** Стабильная ссылка для снятия обработчика visibilitychange. */
  visibility: () => void;
}

/** Ждём метаданные кадра источника с границей: без кадра запуск обязан упасть, а не зависнуть. */
function waitForMetadata(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA && video.videoWidth > 0) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    let timer = 0;
    const done = (settle: () => void): void => {
      window.clearTimeout(timer);
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("error", onError);
      settle();
    };
    const onLoaded = (): void => done(resolve);
    const onError = (): void => done(() => reject(new Error("vbg: источник видео сообщил об ошибке")));
    timer = window.setTimeout(
      () => done(() => reject(new Error("vbg: метаданные кадра не пришли"))),
      METADATA_TIMEOUT_MS,
    );
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("error", onError);
  });
}

class VbgEngine {
  private worker: Worker | null = null;
  private readyPromise: Promise<void> | null = null;
  private preloadResolve: (() => void) | null = null;
  private preloadReject: ((err: Error) => void) | null = null;

  private session: VbgSession | null = null;
  /** Ожидание последнего кадра при остановке: `result` не несёт идентификатора сессии. */
  private drain: (() => void) | null = null;
  /** Штампы кадров монотонны в пределах жизни модели MediaPipe, а не сессии. */
  private lastStamp = 0;

  /** Поднять воркер и прогреть модель. Повторные вызовы переиспользуют тот же промис. */
  ensureReady(): Promise<void> {
    if (this.readyPromise) return this.readyPromise;
    const promise = this.preload();
    this.readyPromise = promise;
    // Отказ не кэшируем: следующая попытка обязана поднять воркер заново.
    promise.catch(() => {
      if (this.readyPromise === promise) this.readyPromise = null;
      this.destroyWorker();
    });
    return promise;
  }

  /** Запустить обработку трека. Прошлая сессия гасится, воркер и модель не трогаются. */
  async start(opts: VbgStartOptions): Promise<MediaStreamTrack> {
    await this.ensureReady();
    await this.stop();

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    // Уводим за край вместо display:none: у невидимого для композитора видео rVFC может не звать.
    video.style.position = "fixed";
    video.style.left = "-10000px";
    video.style.width = "1px";
    video.style.height = "1px";
    video.style.pointerEvents = "none";
    video.srcObject = new MediaStream([opts.track]);
    document.body.append(video);
    void video.play().catch(() => {
      // play() у живого трека может завершиться не сразу: кадры нужны, но старт от него не зависит.
    });

    try {
      await waitForMetadata(video);
    } catch (err) {
      this.releaseVideo(video);
      throw err;
    }
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      this.releaseVideo(video);
      throw new Error("vbg: источник отдал кадр без размера");
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) {
      this.releaseVideo(video);
      throw new Error("vbg: нет 2D-контекста выходного канваса");
    }
    const outputTrack = canvas.captureStream().getVideoTracks()[0];
    if (!outputTrack) {
      this.releaseVideo(video);
      throw new Error("vbg: captureStream не дал видеотрек");
    }

    const session: VbgSession = {
      video,
      ctx,
      outputTrack,
      awaiting: false,
      pending: false,
      rvfc: 0,
      timer: 0,
      visibility: () => {},
    };
    session.visibility = () => this.onVisibility(session);

    this.session = session;
    this.post({
      type: "start",
      setup: {
        width,
        height,
        bg: opts.mode === "background" ? "image" : "blur",
        blur: opts.blur,
        backgroundUrl: opts.mode === "background" ? opts.backgroundUrl ?? null : null,
      },
    });
    this.beginPump(session);
    document.addEventListener("visibilitychange", session.visibility);
    return outputTrack;
  }

  /** Живая смена параметров; без активной сессии — no-op. */
  update(patch: VbgConfigPatch): void {
    if (!this.session || !this.worker) return;
    this.post({ type: "config", patch });
  }

  /** Погасить сессию. Идемпотентно; воркер и модель остаются прогретыми. */
  async stop(): Promise<void> {
    const session = this.session;
    if (!session) return;
    this.session = null;
    this.cancelPump(session);
    document.removeEventListener("visibilitychange", session.visibility);

    // Воркер отвечает `result` на последний кадр до `stop`; дожидаемся ответа, чтобы он не
    // пришёл уже в следующую сессию (в протоколе нет идентификатора кадра).
    if (session.awaiting) {
      await new Promise<void>((resolve) => {
        const timer = window.setTimeout(() => {
          this.drain = null;
          resolve();
        }, DRAIN_TIMEOUT_MS);
        this.drain = () => {
          window.clearTimeout(timer);
          this.drain = null;
          resolve();
        };
      });
    }

    this.releaseVideo(session.video);
    session.outputTrack.stop();
    this.post({ type: "stop" });
  }

  // ── воркер ────────────────────────────────────────────────────────────────────────────────

  private get delegate(): VbgDelegate {
    // GPU-делегат MediaPipe (WebGL2) доступен и в Firefox: на 720p инференс ~9 мс против ~50 мс
    // у CPU-делегата. Если WebGL2/делегат не поднимется, ensureModel сам откатится на CPU.
    return "GPU";
  }

  private post(message: VbgPreloadMessage | VbgStartMessage | VbgConfigMessage | VbgStopMessage): void {
    this.worker?.postMessage(message);
  }

  private async preload(): Promise<void> {
    // Литеральные URL: Vite обязан собрать ОБА воркера, выбор — по браузеру.
    const worker =
      browserName === "Firefox"
        ? new Worker(new URL("./worker-v2.ts", import.meta.url), { type: "module" })
        : new Worker(new URL("./worker-v5.ts", import.meta.url), { type: "module" });
    this.worker = worker;
    await new Promise<void>((resolve, reject) => {
      this.preloadResolve = resolve;
      this.preloadReject = reject;
      worker.onmessage = (event: MessageEvent) => this.onMessage(event);
      worker.onerror = (event: ErrorEvent) => this.onWorkerError(event);
      worker.postMessage({ type: "preload", delegate: this.delegate } satisfies VbgPreloadMessage);
    });
    this.preloadResolve = null;
    this.preloadReject = null;
  }

  private destroyWorker(): void {
    this.worker?.terminate();
    this.worker = null;
    this.preloadResolve = null;
    this.preloadReject = null;
  }

  private onMessage(event: MessageEvent): void {
    const data = event.data as VbgOutgoingMessage;
    switch (data.type) {
      case "ready":
        this.preloadResolve?.();
        this.preloadResolve = null;
        this.preloadReject = null;
        return;
      case "log":
        this.forwardLog(data);
        return;
      case "result":
        this.onResult(data.bitmap);
        return;
      case "error":
        this.onWorkerFailure(new Error(data.error));
        return;
      case "disposed":
        return;
    }
  }

  private onWorkerError(event: ErrorEvent): void {
    const where = event.filename ? ` (${event.filename}:${event.lineno}:${event.colno})` : "";
    const message = event.message || (event.error instanceof Error ? event.error.message : "") || "без сообщения";
    this.onWorkerFailure(new Error(`${message}${where}`));
  }

  private onWorkerFailure(error: Error): void {
    const reject = this.preloadReject;
    if (reject) {
      this.preloadReject = null;
      this.preloadResolve = null;
      reject(error);
      return;
    }
    log.error("vbg worker", error.message);
    // Ответа по упавшему кадру не будет: разрешаем возможный drain сразу, иначе stop() ждёт впустую.
    this.drain?.();
    const session = this.session;
    if (session) {
      session.awaiting = false;
      void this.stop();
    }
  }

  private forwardLog(message: VbgLogMessage): void {
    switch (message.level) {
      case "debug":
        log.debug("vbg:", message.text);
        return;
      case "info":
        log.info("vbg:", message.text);
        return;
      case "warn":
        log.warn("vbg:", message.text);
        return;
      case "error":
        log.error("vbg:", message.text);
        return;
    }
  }

  // ── кадры ─────────────────────────────────────────────────────────────────────────────────

  private onResult(bitmap: ImageBitmap): void {
    const session = this.session;
    if (!session) {
      bitmap.close();
      this.drain?.();
      return;
    }
    session.awaiting = false;
    session.ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    reportProcessedFrame();
    // Пока воркер был занят, пришёл свежий кадр — берём его сразу, не дожидаясь следующего rVFC:
    // иначе период кадра становится «время обработки + период источника» и fps проседает почти вдвое.
    if (session.pending) {
      session.pending = false;
      this.postFrame(session);
    }
  }

  private postFrame(session: VbgSession): void {
    if (this.session !== session) return;
    if (session.awaiting) {
      session.pending = true;
      return;
    }
    const video = session.video;
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;

    this.lastStamp = Math.max(performance.now(), this.lastStamp + 0.001);
    let frame: VideoFrame;
    try {
      frame = new VideoFrame(video, { timestamp: Math.round(this.lastStamp * 1000) });
    } catch (err) {
      log.warn("vbg: VideoFrame из кадра не построился", err);
      return;
    }
    const worker = this.worker;
    if (!worker) {
      frame.close();
      return;
    }
    session.awaiting = true;
    try {
      worker.postMessage({ type: "frame", frame, ts: this.lastStamp }, [frame]);
    } catch (err) {
      session.awaiting = false;
      frame.close();
      log.warn("vbg: кадр не ушёл в воркер", err);
    }
  }

  /** Кадры отдаёт платформа (rVFC), а на скрытой вкладке — таймер: видео там кадров не шлёт. */
  private beginPump(session: VbgSession): void {
    this.cancelPump(session);
    const video = session.video;
    if (!document.hidden && typeof video.requestVideoFrameCallback === "function") {
      const tick = (): void => {
        if (this.session !== session) return;
        session.rvfc = video.requestVideoFrameCallback(tick);
        this.postFrame(session);
      };
      session.rvfc = video.requestVideoFrameCallback(tick);
      return;
    }
    const tick = (): void => {
      if (this.session !== session) return;
      this.postFrame(session);
      session.timer = window.setTimeout(tick, PUMP_INTERVAL_MS);
    };
    session.timer = window.setTimeout(tick, PUMP_INTERVAL_MS);
  }

  private cancelPump(session: VbgSession): void {
    if (session.rvfc) {
      session.video.cancelVideoFrameCallback(session.rvfc);
      session.rvfc = 0;
    }
    if (session.timer) {
      window.clearTimeout(session.timer);
      session.timer = 0;
    }
  }

  private onVisibility(session: VbgSession): void {
    if (this.session !== session) return;
    if (document.hidden) void startEmptyAudioTrack();
    else endEmptyAudioTrack();
    this.beginPump(session);
  }

  private releaseVideo(video: HTMLVideoElement): void {
    video.pause();
    video.srcObject = null;
    video.remove();
  }
}

export const vbgEngine = new VbgEngine();
